import { Skeleton, Typography } from '@mui/joy';
import { ContentWSectionHeading, ScrollSection } from '.';
import { Datatable, DatatableProps } from '../Datatable/Datatable';
import { useData } from '../../providers/DataProvider';
import { useMemo } from 'react';
import { useDataFormPage } from '../../providers/DataFormPageProvider';

export interface DataAppendixProps<T extends Record<string, string | number>>
	extends Omit<DatatableProps<T>, 'rows'> {
	isFetching?: boolean;
	name?: string;
	heading?: string;
	htmlText: string;
}

const raceDecoder = (race: string) => {
	if (race === 'B') {
		return 'Black';
	}
	if (race === 'W') {
		return 'White';
	}
	if (race === 'na') {
		return 'NA';
	}
	if (race === 'L') {
		return 'Latina';
	}
	if (race === 'H') {
		return 'Latinas';
	}
	if (race === 'MENA') {
		return 'Middle Eastern / North African';
	}
	if (race === 'NatAm') {
		return 'Native American';
	}
	if (race === 'AP') {
		return 'Asian American/Pacific Islander';
	}
	return race;
};
const decodeRace = (race: string, secondaryRace: string) => {
	if (secondaryRace !== 'na') {
		return secondaryRace
			.split('/')
			.map((r) => raceDecoder(r.trim()))
			.join(' / ');
	}
	return raceDecoder(race);
};

const decodeStatus = (row: { [x: string]: string }) => {
	if (row['primary_challenger'] === '1') {
		return 'Primary Challenger';
	}
	if (
		row['incumbent'] === '1' &&
		row['challenger'] === '0' &&
		row['open'] === '0'
	) {
		return 'Incumbent';
	}
	if (
		row['incumbent'] === '0' &&
		row['challenger'] === '1' &&
		row['open'] === '0'
	) {
		return 'Challenger';
	}
	if (
		row['incumbent'] === '0' &&
		row['challenger'] === '0' &&
		row['open'] === '1'
	) {
		return 'Open Seat';
	}
};

export const DataAppendix = <T extends Record<string, string | number>>({
	isFetching,
	name = 'dataAppendix',
	heading = 'Data Appendix',
	htmlText,
	...props
}: DataAppendixProps<T>) => {
	const { page } = useDataFormPage();
	const {
		stateEdgeCase10Data,
		stateHouse10Data,
		stateSenate10Data,
		usHouse10Data,
		usSenate10Data,
		usHouseData,
		usSenateData
	} = useData();

	const rows = useMemo(() => {
		const returnData = [];
		if (page === 'national') {
			if (!(usHouseData && usSenateData)) {
				return [];
			}
			if (usHouseData !== null) {
				returnData.push(...usHouseData);
			}
			if (usSenateData !== null) {
				returnData.push(...usSenateData);
			}
		} else if (page === 'state') {
			if (
				!(
					(stateEdgeCase10Data && stateHouse10Data && stateSenate10Data) ||
					(usHouse10Data && usSenate10Data)
				)
			) {
				return [];
			}
			if (stateEdgeCase10Data !== null) {
				returnData.push(...stateEdgeCase10Data);
			}
			if (stateHouse10Data !== null) {
				returnData.push(...stateHouse10Data);
			}
			if (stateSenate10Data !== null) {
				returnData.push(...stateSenate10Data);
			}
			if (usHouse10Data !== null) {
				returnData.push(...usHouse10Data);
			}
			if (usSenate10Data !== null) {
				returnData.push(...usSenate10Data);
			}
		}
		const filteredReturnData = [];
		for (let index = 0; index < returnData.length; index++) {
			const element = returnData[index];
			const currentIndex = filteredReturnData.findIndex(
				(r) => r['cand_name'] === element['cand_name']
			);
			if (currentIndex < 0 && element['cand_gender'] === 'Woman') {
				filteredReturnData.push(element);
			}
		}

		return filteredReturnData.map((row) => ({
			...row,
			candRace: decodeRace(row['cand_race'], row['cand_race_multiple']),
			status: decodeStatus(row),
			totalRaised: Number(row['total_raised']),
			propSelf: Number(row['prop_self']),
			propSmall: Number(row['prop_small']),
			party: row['party'].replace('party', '').trim()
		})) as unknown as T[];
	}, [
		stateEdgeCase10Data,
		stateHouse10Data,
		stateSenate10Data,
		usHouse10Data,
		usSenate10Data,
		usHouseData,
		usSenateData,
		page
	]);

	return (
		<ScrollSection name={name}>
			<ContentWSectionHeading heading={heading}>
				<Typography level='body1'>
					<Skeleton loading={isFetching ? isFetching : false}>
						<div
							dangerouslySetInnerHTML={{
								__html: htmlText
							}}
						/>
					</Skeleton>
				</Typography>
				<Datatable {...props} rows={rows} />
			</ContentWSectionHeading>
		</ScrollSection>
	);
};
